import React from 'react';
import { connect } from 'react-redux';
import { change, Field, InjectedFormProps, reduxForm } from 'redux-form';
import { AppState } from '@Redux/store';
import validate, { CreateDraftParams } from './DraftValidate';
import { CardBody } from 'reactstrap';
import Repeater from '@Utils/Repeater';
import Card from '../utils/Card';
import { CardDisplay } from '../utils/CardDisplay';
import { Dispatch } from '@Redux/type';
import { container } from '@Container';
import { TYPES } from '@Types';
import { MapsAction } from '@Redux/actions/ninthAge/MapsAction';
import { MapView } from '@Selectors/tool/pairing/create/view/MapView';
import { selectTablesPresenter } from '@Selectors/tool/pairing/create/selectTablesPresenter';
import ReduxFormTableBlock from '@Utils/ReduxFormTableBlock';
import { FormattedMessage } from 'react-intl';
import { defaultMessages } from '@I18n/default';

interface Props {
  onSubmit: (values: CreateDraftParams) => void;
  previousPage: () => void;
}

const Form: React.FC<
  FormProps & InjectedFormProps<CreateDraftParams, FormProps>
> = ({
  selection,
  handleSubmit,
  fetchData,
  selectTable,
  completeTable,
  unselectTable,
  previousPage,
}) => {
  React.useEffect(() => {
    fetchData();
  }, []);

  if (selection.hasError) {
    return (
      <p>
        <FormattedMessage {...defaultMessages.toolPairingSelectArmiesError} />
      </p>
    );
  }

  if (selection.isMapsLoading) {
    return <div className="loader"></div>;
  }

  if (!selection.isMapsLoading && selection.maps.length === 0) {
    const placeholders: MapView[] = Array(selection.tables.length).fill(MapView.empty());
    completeTable(placeholders);
  }

  return (
    <form onSubmit={handleSubmit}>
      <CardBody>
        <div>
          <Field
            name="tables"
            datas={selection.tables}
            unselectTable={unselectTable}
            component={ReduxFormTableBlock}
            disabled={!selection.isMapsLoading && selection.maps.length === 0}
            label={defaultMessages.toolPairingFormTablesLabel}
          />
          <div className="justify-content-center">
            <button className="btn btn-age btn-lg m-3" onClick={previousPage}>
              <FormattedMessage {...defaultMessages.formCancel} />
            </button>
            <button className="btn btn-age btn-lg m-3">
              <FormattedMessage {...defaultMessages.formValidate} />
            </button>
          </div>
          {!selection.isMapsLoading && selection.maps.length === 0 && (
            <p>
              <FormattedMessage
                {...defaultMessages.toolPairingFormTablesNoMaps}
              />
            </p>
          )}
          <div className="row justify-content-md-center list-armies">
            <Repeater data={selection.maps} indexPrefix="map">
              {(item: MapView) => (
                <Card
                  display={CardDisplay.TABLE}
                  selectable={item.selectable}
                  table={item}
                  selected={item.selected}
                  select={() => selectTable(selection.tables, item)}
                />
              )}
            </Repeater>
          </div>
        </div>
      </CardBody>
    </form>
  );
};

const CreateDraftTableForm = reduxForm<CreateDraftParams, FormProps>({
  form: 'create_draft',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  validate,
})(Form);

const mapStateToProps = (state: AppState) => ({
  selection: selectTablesPresenter(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  const mapsAction = container.get<MapsAction>(TYPES.Actions.MapsAction);

  return {
    fetchData: () => dispatch(mapsAction.fetchData()),
    selectTable: (tables: Array<MapView>, map: MapView) => {
      const seleted = tables
        .filter((item) => item.mapId)
        .concat(MapView.fromMap(map.map!, true, true));
      dispatch(change('create_draft', 'tables', seleted));
    },
    completeTable: (selection: MapView[]) => {
      dispatch(change('create_draft', 'tables', selection));
    },
    unselectTable: (tables: Array<MapView>, map: MapView) => {
      const seleted = tables
        .filter((item) => item.mapId)
        .filter((item) => item.mapId !== map.mapId);
      dispatch(change('create_draft', 'tables', seleted));
    },
  };
};

type FormProps = Props &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateDraftTableForm);
